<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { listPosts, ApiError, type ChecklistPost } from '@/api'
import { currentLocale } from '@/i18n'
import DrillList from '@/components/DrillList.vue'

const { t } = useI18n()

const posts = ref<ChecklistPost[]>([])
const loading = ref(true)
const loadError = ref('')

// Account > calendar title > chat, then one card per post (a level with a single value is skipped).
const levels = [
  { label: (post: ChecklistPost) => post.account_name },
  { label: (post: ChecklistPost) => post.title },
  { label: (post: ChecklistPost) => String(post.chat_id) },
]
const texts = (post: ChecklistPost) => [post.account_name, post.title, String(post.chat_id)]

// Only what differs from a fresh post: "Sort: due ↓ · Filter: open · Group: category · Page 2".
function configOf(post: ChecklistPost): string {
  const parts: string[] = []
  if (post.sort_category !== 'position') {
    parts.push(`${t('posts.config.sort')}: ${post.sort_category} ${post.sort_direction === 'desc' ? '↓' : '↑'}`)
  }
  if (post.filter_status !== 'all') parts.push(`${t('posts.config.filter')}: ${post.filter_status}`)
  if (post.filter_tag) parts.push(`${t('posts.config.filter')}: #${post.filter_tag}`)
  if (post.group_by !== 'none') parts.push(`${t('posts.config.group')}: ${post.group_by}`)
  if (post.page > 0) parts.push(`${t('posts.config.page')} ${post.page + 1}`)
  return parts.length ? parts.join(' · ') : t('posts.config.default')
}

function formatDate(value: string | null): string {
  if (!value) return t('posts.never')
  try {
    return new Date(value).toLocaleString(currentLocale())
  } catch {
    return value
  }
}

async function load() {
  loading.value = true
  loadError.value = ''
  try {
    posts.value = await listPosts()
  } catch (e) {
    loadError.value = e instanceof ApiError ? e.message : t('posts.loadFailed')
  } finally {
    loading.value = false
  }
}

onMounted(load)
</script>

<template>
  <div>
    <p class="hint">{{ t('posts.intro') }}</p>

    <p v-if="loading">{{ t('common.loading') }}</p>
    <p v-else-if="loadError" class="error">{{ loadError }}</p>
    <p v-else-if="posts.length === 0" class="hint">{{ t('posts.none') }}</p>
    <DrillList
      v-else
      :items="posts"
      :levels="levels"
      :texts="texts"
      :item-key="(post: ChecklistPost) => post.id"
      query-key="posts"
      :placeholder="t('drill.search')"
    >
      <template #item="{ item: post, chosen }">
        <dl>
          <div v-if="!chosen.includes(0)" class="field">
            <dt>{{ t('posts.col.account') }}</dt>
            <dd>{{ post.account_name }}</dd>
          </div>
          <div v-if="!chosen.includes(1)" class="field">
            <dt>{{ t('posts.col.title') }}</dt>
            <dd>{{ post.title }}</dd>
          </div>
          <div v-if="!chosen.includes(2)" class="field">
            <dt>{{ t('posts.col.chat') }}</dt>
            <dd>{{ post.chat_id }}</dd>
          </div>
          <div class="field">
            <dt>{{ t('posts.col.tasks') }}</dt>
            <dd>{{ t('posts.done', { done: post.completed_task_count, total: post.task_count }) }}</dd>
          </div>
          <div class="field">
            <dt>{{ t('posts.col.synced') }}</dt>
            <dd>{{ formatDate(post.last_synced_at) }}</dd>
          </div>
          <div class="field">
            <dt>{{ t('posts.col.config') }}</dt>
            <dd>{{ configOf(post) }}</dd>
          </div>
          <div class="field">
            <dt>{{ t('posts.col.mode') }}</dt>
            <dd>
              <span class="badge" :class="post.render_mode">{{ post.render_mode }}</span>
            </dd>
          </div>
        </dl>
      </template>
    </DrillList>
  </div>
</template>

<style scoped>
.badge {
  font-size: 0.75rem;
  padding: 0.1rem 0.4rem;
  border-radius: 0.5rem;
  background: var(--tg-secondary-bg-color);
  color: var(--tg-hint-color);
}

.badge.native {
  color: #34c759;
}

.hint {
  color: var(--tg-hint-color);
  font-size: 0.85rem;
}

.error {
  color: var(--tg-destructive-text-color);
}
</style>
